/**
 * Does the wizard's Content step apply to this product selection? (NPJB-517)
 *
 * A 3rd Eye **weekly data feed** is a data delivery, not a campaign — there are
 * no assets to attach, so walking the user through Content is dead weight. When
 * the weekly feed is the *entire* order the wizard drops the step and routes
 * Products → Review.
 *
 * "Entire order" is strict on both axes: exactly one product (3rd Eye) and
 * exactly one delivery option (the weekly feed). Ticking "Intent as an Audience"
 * alongside it, or buying any second product, brings Content back.
 * `te-feed-data` is a data-type choice rather than a delivery option (NPJB-516),
 * so it never enters the decision.
 *
 * The constants mirror `THIRD_EYE_*` in
 * `dzone-backend/app/application/audience/request_schemas.py` — the same answers
 * the backend reads to compute the `'third-eye'` product mix. Nothing on the
 * backend requires Content data at submit (`submit_existing_draft` checks only
 * status), so the skip is purely a routing concern. Store-free and `@/app`-free,
 * like the rest of `@/lib/products`.
 */
import {
  asStringArray,
  type ProductAnswersMap,
  type ProductSelectionInput,
} from './derive-products';

export const THIRD_EYE_SLUG = '3rd-eye';
export const THIRD_EYE_DELIVERY_FIELD = 'te-delivery';
export const THIRD_EYE_WEEKLY_FEED_OPTION = 'te-weekly-feed';

export interface ContentStepInputs {
  productSelections: readonly ProductSelectionInput[];
  productAnswers: ProductAnswersMap;
}

/**
 * True when the order is nothing but a 3rd Eye weekly data feed.
 *
 * Counts *selections* rather than resolved slugs, so a legacy row carrying only
 * a `name` still blocks the skip — an unrecognised second product must never
 * silently drop the Content step.
 */
export const isWeeklyFeedOnlyOrder = ({
  productSelections,
  productAnswers,
}: ContentStepInputs): boolean => {
  const selections = productSelections ?? [];
  if (selections.length !== 1) return false;
  if (selections[0]?.slug?.trim().toLowerCase() !== THIRD_EYE_SLUG) return false;

  const delivery = asStringArray(productAnswers?.[THIRD_EYE_SLUG]?.[THIRD_EYE_DELIVERY_FIELD]);
  return delivery.length === 1 && delivery[0] === THIRD_EYE_WEEKLY_FEED_OPTION;
};
